import React, { useState } from "react"
import { Divider, ModalEcomm, IconButton } from "@ncs/web-legos/components"
import { TextField } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { SVGViewer } from "./SVGViewer"
import {
	columnContainerStyle,
	pageContainerStyle,
	productListStyle,
	searchBarStyle,
	searchIconStyle,
} from "./styles"
import { PartList } from "./ProductList/ProductList"
import { AddedToCartModalEcomm } from "~/shared-components"
import { useExplodedViewParts } from "./hooks/useExplodedViewParts"
import { SortControl } from "./SortControl"
import { ExplodedViewItem, useExplodedViewDetail } from "@ncs/ncs-api/portal-apps"

type ExplodedViewModalProps = {
	onClose: () => void
	isOpen: boolean
	currentExplodedView: ExplodedViewItem | null
}

export const ExplodedViewModal: React.FC<ExplodedViewModalProps> = ({
	isOpen,
	onClose,
	currentExplodedView,
}) => {
	const [selectedHotspot, setSelectedHotspot] = useState<string>("")
	const [addedPartId, setAddedPartId] = useState<string | null>(null)
	const [explodedViewDetail, loadingExplodedViewDetail] = useExplodedViewDetail(
		currentExplodedView?.id ?? ""
	)
	const { searchTerm, setSearchTerm, sortOption, setSortOption, partsToDisplay } =
		useExplodedViewParts(explodedViewDetail?.parts ?? [])

	return (
		<ModalEcomm maxWidth="xl" isOpen={isOpen} showCloseButton={false} onClose={onClose}>
			<header>
				<h2>{currentExplodedView?.name ?? ""}</h2>
			</header>
			<Divider />
			<section css={columnContainerStyle}>
				<section css={pageContainerStyle}>
					{loadingExplodedViewDetail ?
						<Skeleton
							variant="rect"
							width="100%"
							height={676}
							style={{ borderRadius: 8 }}
						/>
					:	<SVGViewer
							svgUrl={explodedViewDetail?.svgUrl ?? ""}
							onSelectHotspot={setSelectedHotspot}
							selectedPartNumber={selectedHotspot}
						/>
					}
				</section>
				<section css={productListStyle}>
					<TextField
						css={searchBarStyle}
						placeholder="Search Component or Drawing Number"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						InputProps={{
							disableUnderline: true,
							endAdornment: (
								<IconButton
									css={searchIconStyle}
									icon="search"
									aria-label="Search"
									onClick={() => undefined}
								/>
							),
						}}
					/>
					<Divider css={{ margin: "10px 0" }} />
					<SortControl value={sortOption} onChange={setSortOption} />
					<Divider css={{ margin: "10px 0" }} />
					{loadingExplodedViewDetail ?
						<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
							{Array.from({ length: 5 }).map((_, i) => (
								<div
									key={i}
									style={{
										display: "grid",
										gridTemplateColumns: "24px 54px 1fr auto",
										gap: 12,
										paddingBottom: 10,
										borderBottom: "1px solid #d1d5db",
									}}
								>
									<Skeleton variant="circle" width={24} height={24} />
									<Skeleton
										variant="rect"
										width={54}
										height={54}
										style={{ borderRadius: 4 }}
									/>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: 6,
										}}
									>
										<Skeleton variant="rect" width="80%" height={16} />
										<Skeleton variant="rect" width="50%" height={14} />
										<Skeleton variant="rect" width="30%" height={14} />
										<Skeleton variant="rect" width="25%" height={20} />
									</div>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: 8,
										}}
									>
										<Skeleton
											variant="rect"
											width={88}
											height={50}
											style={{ borderRadius: 4 }}
										/>
										<Skeleton variant="rect" width={88} height={14} />
									</div>
								</div>
							))}
						</div>
					:	<PartList
							selectedHotspot={selectedHotspot}
							onSetSelectedHotspot={setSelectedHotspot}
							onAddToCart={(partId) => setAddedPartId(partId)}
							parts={partsToDisplay}
						/>
					}
				</section>
			</section>
			<AddedToCartModalEcomm
				isOpen={!!addedPartId}
				partId={addedPartId ?? undefined}
				onClose={() => setAddedPartId(null)}
			/>
		</ModalEcomm>
	)
}
